import { computed, type ComputedRef, reactive, readonly, type Ref, toRef, watch } from 'vue';

import { until } from '@vueuse/core';

import { useTimeUnix } from '@/composables/useTimeUnix';
import { useTransactions } from '@/composables/useTransactions';
import { filterDefined } from '@/helpers/arrays';
import { assertUnreachable } from '@/helpers/assert';
import { placeholderBackgroundColor } from '@/helpers/colors';
import {
  capitalize,
  formatAmount,
  formatRate,
  formatReversedTokenRate,
  formatTimestampDate,
  formatTimestampUTC,
  formatTokenAmount,
  formatTokenRate,
  formatTokensRate
} from '@/helpers/formatters';
import { formatAsHHMMSS } from '@/helpers/formatters/datetime';
import type { MaybePromise } from '@/helpers/promises';
import { asyncSleep } from '@/helpers/promises';
import {
  getBottomAmount,
  getBottomAmountToken,
  getFallbackImage,
  getFallbackText,
  getLoaderColor,
  getTitle,
  getTopAmount,
  isBottomAmountStrikeThrough,
  isBottomAmountWithSkeleton,
  isTopAmountBordered,
  isTopAmountStrikeThrough,
  isTopAmountWithSkeleton
} from '@/helpers/transactions';
import { logger } from '@/logs/datadog';
import {
  AmountMode,
  type DetailsItem,
  DetailsItemDateValueFormat,
  type DetailsItemFee,
  DetailsItemType,
  FailReasonType,
  type StatusDetailsItem,
  type Transaction,
  TransactionStatusOTC,
  TransactionType
} from '@/references/axios/transactions/types';
import { SECOND } from '@/references/constants';
import { t } from '@/references/i18n';
import { settings } from '@/references/settings/globals';
import type { Token } from '@/references/tokens';

import { type UiButtonMod } from '@/components/ui/UiButton.types';

interface UseTransactionDetailsReturn {
  opened: Readonly<Ref<boolean>>;
  transactionId: Readonly<Ref<string | undefined>>;

  open(transaction: Transaction): void;

  openById(id: string): Promise<void>;

  close(): void;

  transaction: ComputedRef<Transaction | undefined>;

  title: ComputedRef<string>;
  description: ComputedRef<string | undefined>;
  iconSrc: ComputedRef<string | undefined>;
  fallbackText: ComputedRef<string>;
  fallbackColor: ComputedRef<string>;
  fallbackSrc: ComputedRef<string | undefined>;
  loaderColor: ComputedRef<string | undefined>;
  loading: ComputedRef<boolean>;
  failed: ComputedRef<boolean>;
  longPending: ComputedRef<boolean>;
  failReason: ComputedRef<string>;

  topAmount: ComputedRef<string>;
  topAmountMuted: ComputedRef<boolean>;
  topAmountHighlighted: ComputedRef<boolean>;
  topAmountBordered: ComputedRef<boolean>;
  topAmountStrikeThrough: ComputedRef<boolean>;
  topAmountWithSkeleton: ComputedRef<boolean>;

  bottomAmount: ComputedRef<string | undefined>;
  bottomAmountMuted: ComputedRef<boolean>;
  bottomAmountToken: ComputedRef<Token | undefined>;
  bottomAmountWithSkeleton: ComputedRef<boolean>;
  bottomAmountHighlighted: ComputedRef<boolean>;
  bottomAmountStrikeThrough: ComputedRef<boolean>;

  details: ComputedRef<DisplayedDetailsItem[]>;
  statusDetails: ComputedRef<StatusDisplayedDetailsItem[]>;
  feeValues: ComputedRef<DetailsItemFee | undefined>;

  buttons: ComputedRef<ButtonDescriptor[]>;

  /* Status modal. */
  statusModalOpened: Ref<boolean>;
  openStatusModal: () => void;
  closeStatusModal: () => void;
  statusModalText: ComputedRef<string | undefined>;

  /* Fee modal. */
  feeModalOpened: Ref<boolean>;
  openFeeModal: () => void;
  closeFeeModal: () => void;

  openChat: () => void;
  downloadReceipt: () => void;
}

export type DisplayedDetailsItem = {
  key: string;
  type: DetailsItemType;
  name: string;
  iconUrl?: string;
  link?: string;
  copy?: string;
  vertical?: boolean;
  value: string;
  skeleton: boolean;
  marginBottom: boolean;
  coloredCircleColor?: string;
  coloredCircleIcon?: string;
};

export type StatusDisplayedDetailsItem = {
  key: string;
  type: StatusDetailsItem['type'];
  name: string;
  value: string;
  skeleton: boolean;
};

export type ButtonDescriptor = {
  key: string;
  title: string;
  loading?: boolean;
  disabled?: boolean;
  mod?: UiButtonMod;
  handler?: () => MaybePromise<void>;
};

const state = reactive<{
  opened: boolean;
  statusModalOpened: boolean;
  feeModalOpened: boolean;
  transactionId: string | undefined;
}>({
  opened: false,
  statusModalOpened: false,
  feeModalOpened: false,
  transactionId: undefined
});

function open(transaction_: Transaction): void {
  if (!transaction_.flags.openable) {
    return;
  }

  state.transactionId = transaction_.id;
  state.opened = true;
}

const { transactions, loaded } = useTransactions();

async function openById(id: string): Promise<void> {
  // what for whenever unlocks first
  //
  // should never happen, yet it's possible to capture such brief moment on transitional states
  await Promise.race([until(loaded).toBe(true), asyncSleep(3 * SECOND)]);

  const tx = transactions.value.find((transaction_) => transaction_.id === id);
  if (tx === undefined) {
    return;
  }

  return open(tx);
}

function close(): void {
  state.opened = false;
  state.transactionId = undefined;
}

const transaction = computed((): Transaction | undefined => {
  if (state.transactionId === undefined) {
    return undefined;
  }

  return transactions.value.find((tx) => tx.id === state.transactionId);
});

const title = computed((): string => getTitle(transaction.value));

const description = computed((): string | undefined => {
  if (transaction.value === undefined) {
    return undefined;
  }

  return formatTimestampUTC(transaction.value.createdAt);
});

const iconSrc = computed((): string | undefined => {
  if (transaction.value === undefined) {
    return '';
  }

  return transaction.value.iconUrl;
});

const fallbackText = computed((): string => getFallbackText(transaction.value));

const fallbackColor = computed((): string =>
  placeholderBackgroundColor(getFallbackText(transaction.value))
);

const fallbackSrc = computed((): string | undefined => getFallbackImage(transaction.value));

const loaderColor = computed((): string | undefined => getLoaderColor(transaction.value));

const loading = computed((): boolean => {
  if (transaction.value === undefined) {
    return false;
  }

  return transaction.value.flags.pending ?? false;
});

const failed = computed((): boolean => {
  if (transaction.value === undefined) {
    return false;
  }

  return transaction.value.flags.failed ?? false;
});

const longPending = computed((): boolean => {
  if (transaction.value === undefined) {
    return false;
  }

  if (transaction.value.expectedSuccessAt === undefined) {
    return false;
  }

  if (!transaction.value.flags.pending) {
    return false;
  }

  const { tsNow } = useTimeUnix();

  return tsNow.value > transaction.value.expectedSuccessAt;
});

const topAmount = computed((): string => getTopAmount(transaction.value));

const topAmountMuted = computed((): boolean => {
  if (transaction.value === undefined) {
    return false;
  }

  return transaction.value.flags.muted ?? false;
});

const topAmountHighlighted = computed((): boolean => {
  if (transaction.value === undefined) {
    return false;
  }

  if (transaction.value.flags.muted) {
    return false;
  }

  return transaction.value.flags.incoming ?? false;
});

const topAmountBordered = computed((): boolean => isTopAmountBordered(transaction.value));

const topAmountStrikeThrough = computed((): boolean => isTopAmountStrikeThrough(transaction.value));

const topAmountWithSkeleton = computed((): boolean => isTopAmountWithSkeleton(transaction.value));

const bottomAmount = computed((): string | undefined => getBottomAmount(transaction.value));

const bottomAmountMuted = computed((): boolean => {
  if (transaction.value === undefined) {
    return false;
  }

  return transaction.value.flags.muted ?? false;
});

const bottomAmountHighlighted = computed((): boolean => {
  if (transaction.value === undefined) {
    return false;
  }

  return false;
});

const bottomAmountStrikeThrough = computed((): boolean =>
  isBottomAmountStrikeThrough(transaction.value)
);

const bottomAmountToken = computed((): Token | undefined =>
  getBottomAmountToken(transaction.value)
);

const bottomAmountWithSkeleton = computed((): boolean =>
  isBottomAmountWithSkeleton(transaction.value)
);

function humanifyMissingValue(value: string): string {
  return capitalize(value.replaceAll('.', ' '));
}

export function mapDetailsItem(item: DetailsItem): DisplayedDetailsItem | undefined {
  try {
    const key = item.key;
    const name = t(`activity.details.${key}`, {}, humanifyMissingValue(key));

    const type = item.type;
    switch (type) {
      case DetailsItemType.Amount:
        return {
          iconUrl: item.iconUrl,
          name,
          value: formatAmount(
            item.value.amount,
            item.value.decimals,
            item.value.currency,
            item.value.amountMode
          ),
          marginBottom: item.marginBottom ?? false,
          skeleton: item.skeleton ?? false,
          key,
          type
        };
      case DetailsItemType.Status:
        if (transaction.value?.statusDetails && transaction.value?.statusDetails.length > 0) {
          return undefined;
        }

        if (transaction.value?.flags.pending) {
          return undefined;
        }

        return {
          name,
          value: t(`activity.status.title.${item.value}`, {}, humanifyMissingValue(item.value)),
          marginBottom: item.marginBottom ?? false,
          skeleton: item.skeleton ?? false,
          key,
          type
        };
      case DetailsItemType.Text:
        return {
          iconUrl: item.iconUrl,
          marginBottom: item.marginBottom ?? false,
          name: name,
          value: item.value,
          skeleton: item.skeleton ?? false,
          key,
          link:
            key === 'onChain.hash' &&
            transaction.value &&
            'explorerLinkUrl' in transaction.value &&
            transaction.value.explorerLinkUrl !== undefined
              ? transaction.value.explorerLinkUrl
              : undefined,
          copy:
            key === 'common.transactionId' && transaction.value && 'id' in transaction.value
              ? transaction.value.id
              : undefined,
          type,
          vertical: ['sepa.reference', 'common.transactionId'].includes(key)
        };
      case DetailsItemType.LocalizedText:
        return {
          iconUrl: item.iconUrl,
          marginBottom: item.marginBottom ?? false,
          name: name,
          value: t(`activity.localized.${item.value}`, {}, humanifyMissingValue(key)),
          skeleton: item.skeleton ?? false,
          key,
          type
        };
      case DetailsItemType.TokenAmount:
        return {
          iconUrl: item.iconUrl,
          marginBottom: item.marginBottom ?? false,
          name: name,
          value: formatTokenAmount(
            item.value.amount,
            item.value.decimals,
            item.value.currency,
            AmountMode.CodeLast
          ),
          skeleton: item.skeleton ?? false,
          key,
          type
        };
      case DetailsItemType.Rate:
        return {
          iconUrl: item.iconUrl,
          marginBottom: item.marginBottom ?? false,
          name: name,
          value: formatRate(
            item.value.amount,
            item.value.decimals,
            item.value.inputCurrency,
            item.value.outputCurrency
          ),
          skeleton: item.skeleton ?? false,
          key,
          type
        };
      case DetailsItemType.TokenRate:
        return {
          iconUrl: item.iconUrl,
          marginBottom: item.marginBottom ?? false,
          name: name,
          value: formatTokenRate(
            item.value.amount,
            item.value.decimals,
            item.value.inputCurrency,
            item.value.outputCurrency
          ),
          skeleton: item.skeleton ?? false,
          key,
          type
        };
      case DetailsItemType.ReversedTokenRate:
        return {
          iconUrl: item.iconUrl,
          marginBottom: item.marginBottom ?? false,
          name: name,
          value: formatReversedTokenRate(
            item.value.amount,
            item.value.decimals,
            item.value.inputCurrency,
            item.value.outputCurrency
          ),
          skeleton: item.skeleton ?? false,
          key,
          type
        };
      case DetailsItemType.TokenToTokenRate:
        return {
          iconUrl: item.iconUrl,
          marginBottom: item.marginBottom ?? false,
          name: name,
          value: formatTokensRate(
            item.value.amount,
            item.value.decimals,
            item.value.inputCurrency,
            item.value.outputCurrency
          ),
          skeleton: item.skeleton ?? false,
          key,
          type
        };
      case DetailsItemType.Date:
        return {
          iconUrl: item.iconUrl,
          marginBottom: item.marginBottom ?? false,
          name: name,
          value:
            item.value.format === DetailsItemDateValueFormat.DateTime
              ? formatTimestampUTC(item.value.timestamp)
              : formatTimestampDate(item.value.timestamp),
          skeleton: item.skeleton ?? false,
          key,
          type
        };
      case DetailsItemType.Link:
        return {
          iconUrl: item.iconUrl,
          marginBottom: item.marginBottom ?? false,
          name: name,
          value: item.value.text,
          link: item.value.link,
          skeleton: item.skeleton ?? false,
          key,
          type
        };
      case DetailsItemType.Fee: {
        const innerItem = mapDetailsItem(item.value.displayedItem);
        if (innerItem === undefined) {
          return;
        }

        return {
          iconUrl: item.iconUrl,
          marginBottom: item.marginBottom ?? false,
          name: name,
          value: innerItem.value,
          skeleton: item.skeleton ?? false,
          type,
          key
        };
      }
      default:
        return assertUnreachable(type, 'Unknown details item type');
    }
  } catch (error) {
    logger.warn(
      'Failed to map transaction details',
      undefined,
      new Error('Failed to map transaction details', { cause: error })
    );
    return undefined;
  }
}

const details = computed((): DisplayedDetailsItem[] => {
  if (transaction.value === undefined) {
    return [];
  }

  return filterDefined(transaction.value.details.map(mapDetailsItem));
});

const statusDetails = computed((): StatusDisplayedDetailsItem[] => {
  if (transaction.value === undefined) {
    return [];
  }

  if (transaction.value.statusDetails === undefined) {
    return [];
  }

  const mapped = transaction.value.statusDetails.map(
    (item): StatusDisplayedDetailsItem | undefined => {
      try {
        const name = t(`activity.details.${item.key}`, {}, humanifyMissingValue(item.key));

        const type = item.type;
        switch (type) {
          case DetailsItemType.Text:
            return {
              name: name,
              value: item.value,
              skeleton: item.skeleton ?? false,
              key: item.key,
              type
            };
          case DetailsItemType.LocalizedText:
            return {
              name: name,
              value: t(`activity.localized.${item.value}`, {}, humanifyMissingValue(item.key)),
              skeleton: item.skeleton ?? false,
              key: item.key,
              type
            };
          case DetailsItemType.Date:
            return {
              name: name,
              value:
                item.value.format === DetailsItemDateValueFormat.DateTime
                  ? formatTimestampUTC(item.value.timestamp)
                  : formatTimestampDate(item.value.timestamp),
              skeleton: item.skeleton ?? false,
              key: item.key,
              type
            };
          case DetailsItemType.Duration:
            return {
              name: name,
              value: formatAsHHMMSS(item.value),
              skeleton: item.skeleton ?? false,
              key: item.key,
              type
            };
          default:
            assertUnreachable(type, 'Unknown details item type');
        }
      } catch (error) {
        logger.warn(
          'Failed to map transaction details',
          undefined,
          new Error('Failed to map transaction details', { cause: error })
        );
        return undefined;
      }
    }
  );

  return filterDefined(mapped);
});

function openStatusModal(): void {
  state.statusModalOpened = true;
}

function closeStatusModal(): void {
  state.statusModalOpened = false;
}

function openFeeModal(): void {
  state.feeModalOpened = true;
}

function closeFeeModal(): void {
  state.feeModalOpened = false;
}

const statusModalText = computed((): string | undefined => {
  if (transaction.value === undefined) {
    return undefined;
  }

  const type = transaction.value.type;
  switch (type) {
    case TransactionType.OTC: {
      switch (transaction.value.status) {
        case TransactionStatusOTC.Processing:
          return t('activity.statusModal.otc.processing');
        case TransactionStatusOTC.Completed:
          return t('activity.statusModal.otc.completed');
        case TransactionStatusOTC.Failed:
          return t('activity.statusModal.otc.failed');
        default:
          return undefined;
      }
    }
    default:
      return undefined;
  }
});

const buttons = computed((): ButtonDescriptor[] => {
  if (transaction.value === undefined) {
    return [];
  }

  // add logic if more tx types appear

  return [];
});

const feeValues = computed((): DetailsItemFee | undefined => {
  if (transaction.value === undefined) {
    return undefined;
  }

  return transaction.value.details.find((d) => d.type === DetailsItemType.Fee);
});

const failReason = computed((): string => {
  if (transaction.value === undefined || transaction.value.failReason === undefined) {
    return t('activity.localized.reason.default');
  }

  if (transaction.value.failReason.type === FailReasonType.Text) {
    return transaction.value.failReason.value;
  }

  return t(`activity.localized.${transaction.value.failReason.value}`);
});

function openChat(): void {
  if (transaction.value === undefined || !settings.chatEnabled) {
    return;
  }

  // TODO: Add chat logic here.
}

async function downloadReceipt(): Promise<void> {
  if (transaction.value === undefined || !settings.statementsEnabled) {
    return;
  }

  return useTransactions().getStatements(transaction.value.id, transaction.value.type);
}

watch(transaction, (tx) => {
  if (tx === undefined) {
    close();
    closeStatusModal();
    closeFeeModal();
  }
});

export const useTransactionDetails = (): UseTransactionDetailsReturn => {
  return {
    opened: readonly(toRef(state, 'opened')),
    transactionId: readonly(toRef(state, 'transactionId')),
    transaction,
    title,
    description,
    iconSrc,
    fallbackText,
    fallbackColor,
    fallbackSrc,
    loaderColor,
    loading,
    failed,
    longPending,
    topAmount,
    topAmountMuted,
    topAmountHighlighted,
    topAmountBordered,
    topAmountStrikeThrough,
    topAmountWithSkeleton,
    bottomAmount,
    bottomAmountMuted,
    bottomAmountToken,
    bottomAmountWithSkeleton,
    bottomAmountHighlighted,
    bottomAmountStrikeThrough,
    open,
    openById,
    close,
    details,
    statusDetails,
    buttons,
    statusModalOpened: readonly(toRef(state, 'statusModalOpened')),
    openStatusModal,
    closeStatusModal,
    feeModalOpened: readonly(toRef(state, 'feeModalOpened')),
    openFeeModal,
    closeFeeModal,
    downloadReceipt,
    statusModalText,
    feeValues,
    openChat,
    failReason
  };
};
